import { useEffect, useRef, useState } from 'react';
import { aiProcessHub, type AIProcessRealtime } from './AIProcessHub';

// ─────────────────────────────────────────────────────────────────────────────
// useWorkflowProgress — cookie-cutter realtime progress hook
// ─────────────────────────────────────────────────────────────────────────────
//
// Pairs with backend Option-C workflows (see CodeFirstWorkflowAttribute).
// Any feature that triggers an AIProcess can plug this hook in to get live
// step-by-step progress without writing per-feature SignalR plumbing.
//
// CONTRACT
//   Pass:
//     • processKey — the opaque Guid the workflow trigger returned
//     • steps      — the step schema (matches WorkflowNode.id values from
//                    your code-first default)
//     • watchKey   — what to invoke('Watch', watchKey) on first connect.
//                    Typically composed from the user's identity.
//
//   Returns reactive state:
//     • status         — running | completed | failed | unknown
//     • completedSteps — Set of step ids that have already shown a log
//     • activeStepId   — best guess at the currently-running step
//     • latestLog      — most recent log description (the agent's
//                        "thinking out loud" line)
//     • isComplete     — true when the workflow's status flipped to
//                        COMPLETED, even if not every step emitted a log
//     • signalRConnected — true when SignalR is alive; consumer may
//                          want to ALSO poll as a fallback when false
//
// HOW IT WORKS
//   On mount: ensures the hub is started, calls watch(watchKey), and
//   registers an 'updated' handler that filters by processKey.
//   On unmount: removes the handler. The hub connection itself stays
//   alive for other consumers.
//
//   The hub fires 'updated' on every AddLog inside the workflow — so we
//   accumulate completed step ids by walking the AIProcess.logs array.
//   The "latest log" is just the most-recent log entry's description.
//
// FALLBACK
//   This hook does NOT poll. Polling is the consumer's responsibility
//   when signalRConnected === false. The PromptReviewDrawer keeps a
//   2.5 s poll loop running as a safety net and reconciles whichever
//   source produced the result first.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One step in a workflow. The id must match the corresponding
 * <c>WorkflowNode.id</c> in the [CodeFirstWorkflow]-decorated default.
 * Drift the id and the stepper won't light up — but the run still works.
 */
export interface WorkflowStep {
  /** Stable id; matches <c>WorkflowNode.id</c> in the C# default. */
  id: string;
  /** Display label for the stepper. */
  label: string;
  /** Optional caption shown below the label when this step is active. */
  description?: string;
}

export type WorkflowStatus = 'running' | 'completed' | 'failed' | 'unknown';

export interface WorkflowProgressState {
  status: WorkflowStatus;
  completedSteps: Set<string>;
  activeStepId: string | null;
  latestLog: string | null;
  isComplete: boolean;
  signalRConnected: boolean;
}

interface Args {
  /** The process key returned by the workflow trigger. */
  processKey: string | null;
  /** Step schema. The order here drives the stepper's order. */
  steps: WorkflowStep[];
  /**
   * Identity to scope the SignalR group subscription. Matches the C#
   * <c>ConversationUserType</c> enum: 1=Member, 2=Merchant, 3=Bot,
   * 4=FamilyContact, 5=System. For Agent Studio operators
   * (TenantManagers) pass 2 (Merchant) and the user's id.
   * If either is null/undefined, the hook still subscribes to events
   * but won't receive any (the user is in no group).
   */
  userType?: number | null;
  userId?: number | null;
  /** Disabled when false — useful when the drawer is closed. */
  enabled?: boolean;
}

const INITIAL: WorkflowProgressState = {
  status: 'unknown',
  completedSteps: new Set(),
  activeStepId: null,
  latestLog: null,
  isComplete: false,
  signalRConnected: false,
};

export function useWorkflowProgress({
  processKey,
  steps,
  userType,
  userId,
  enabled = true,
}: Args): WorkflowProgressState {
  const [state, setState] = useState<WorkflowProgressState>(INITIAL);
  // Order ids for fast lookup of "next step" after a completion.
  const stepIdsRef = useRef<string[]>([]);
  stepIdsRef.current = steps.map((s) => s.id);

  useEffect(() => {
    if (!enabled || !processKey) {
      setState(INITIAL);
      return;
    }

    let cancelled = false;
    const ids = stepIdsRef.current;

    const apply = (process: AIProcessRealtime) => {
      if (cancelled) return;
      if (process.key !== processKey) return;

      const completedSteps = new Set<string>();
      let latestLog: string | null = null;
      for (const log of process.logs ?? []) {
        if (log.stepId && ids.includes(log.stepId)) {
          completedSteps.add(log.stepId);
        }
        if (log.log) latestLog = log.log;
      }

      // Active step = first id from the schema that has NOT been
      // completed yet. After all steps complete we keep activeStepId
      // null — the consumer should switch to the result view.
      let activeStepId: string | null = null;
      for (const id of ids) {
        if (!completedSteps.has(id)) {
          activeStepId = id;
          break;
        }
      }

      let status: WorkflowStatus = 'running';
      const s = (process.status ?? '').toUpperCase();
      if (s === 'COMPLETED') status = 'completed';
      else if (s === 'FAILED') status = 'failed';

      const isComplete = status === 'completed' || activeStepId === null;

      setState({
        status,
        completedSteps,
        activeStepId,
        latestLog,
        isComplete,
        signalRConnected: aiProcessHub.isConnected,
      });
    };

    void (async () => {
      await aiProcessHub.ensureStarted();
      if (cancelled) return;
      if (userType != null && userId != null) {
        await aiProcessHub.watch(userType, userId);
      }
      if (cancelled) return;
      setState((prev) => ({ ...prev, signalRConnected: aiProcessHub.isConnected }));
    })();

    const off = aiProcessHub.onProcessUpdated(apply);
    return () => {
      cancelled = true;
      off();
    };
  }, [processKey, userType, userId, enabled]);

  return state;
}
